import React from 'react';
import Markdown from 'markdown-to-jsx';
import { decodeLiteralTags, sanitizeRichText } from '../../utils/richText';
import * as richTextStyles from './MarkdownContent.module.scss';

const HTML_TAG_RE = /<[a-zA-Z][^>]*>/;

const hasHtml = (text: string) => HTML_TAG_RE.test(text);

interface MarkdownContentProps {
  text: string | object;
  transparent?: boolean;
  renderCallback?: () => void;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ text, transparent = false, renderCallback }) => {
  if (!text) {
    return null;
  }
  const stringifiedText = typeof text === 'object' ? JSON.stringify(text) : String(text);

  // Replace multiple consecutive newlines with a single newline followed by the corresponding number of non-breaking spaces
    // Replace all types of newline characters with <br /> tags
  const formatTextOutsideListItems = (text: string) => {
    const blocks = text.split(/(\n-.*?\n\n)/s); // Split text into blocks by list item start and end

    return blocks.map((block) => {
      // Check if the block starts with a list item
      if (/^\n-/.test(block)) {
        return block; // Return the block as is if it starts with a list item
      }

      return block.replace(/\n{2,}/g, (match) => {
        const newlineCount = match.length; // Count the number of newlines in the match
        const nbspCount = newlineCount - 1; // Number of nbsp needed (newlines - 1)
        let replacement = '\n'; // Start with one newline

        for (let i = 0; i < nbspCount; i += 1) {
          replacement += '&nbsp;\n'; // Add nbsp + newline for each extra newline
        }
        return replacement;
      });
    }).join(''); // Join the blocks back together
  };
  
  const hasUnresolvedTokens = /\{\{.+?\}\}/.test(stringifiedText);
  const formattedText = stringifiedText;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    if (href) {
      window.open(href, '_blank');
    }
  };

  const CustomLink = (props) => (
    <a {...props} onClick={handleLinkClick} />
  );

  const CustomListItem = (props) => {
    const { children } = props;
    const modifiedChildren = React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === 'a') {
        return React.cloneElement(child as React.ReactElement, { onClick: handleLinkClick });
      }
      return child;
    });

    return <li {...props}>{modifiedChildren}</li>;
  };

  const containerStyle = {
    opacity: 1, // You can adjust the opacity level (0.6) as needed
  };

  // useEffect(() => {
  //   if (renderCallback) {
  //     renderCallback();
  //   }
  // }
  // , [renderCallback]);

  if (hasUnresolvedTokens) {
    return <span>{formattedText}</span>;
  }

  // TNBEWEB-1945 — content authored in the screen editor's Text node. Tags the
  // author typed by hand arrive escaped, so decode them BEFORE deciding which
  // branch renders: `&lt;b&gt;bold&lt;/b&gt;` is HTML the user meant, not prose.
  // Markdown stays the branch for content with no tags at all, so nothing that
  // relies on it regresses.
  const decodedText = decodeLiteralTags(formattedText);

  if (hasHtml(decodedText)) {
    return (
      // Links navigate through the `target="_blank"` the sanitizer adds — no
      // React handler here, because the ellipsis path renders this to a string
      // and would drop one. The node's own click actions are suppressed for
      // links in Text.tsx's handleClick.
      <span
        className={richTextStyles.richText}
        dangerouslySetInnerHTML={{ __html: sanitizeRichText(decodedText) }}
      />
    );
  }

  return (
    <Markdown
      options={{
        overrides: {
          a: {
            component: CustomLink,
          },
          li: {
            component: CustomListItem,
          },
        },
      }}
    >
      {formattedText}
    </Markdown>
  );
};

export default MarkdownContent;